import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import DischargeAdviceForm from "./DischargeAdviceForm";
import { createDischargeAdvice, clearError } from "../../../store/slices/dischargeAdviceSlice";
import { fetchPatient } from "../../../store/slices/patientsSlice";

const AddDischargeAdvice = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { caseId, patientId } = state || {};

  const { isLoading, error } = useSelector((state) => state.dischargeAdvice);

  const [formData, setFormData] = useState({
    patientId, // initialize
    caseId,    // initialize
    clientName: "",
    assignedPRP: "",
    psychiatricDiagnosis: "",
    primaryCaretaker: "",
    admissionDate: "",
    dischargeDate: "",
    // dischargeReason: "",
    otherReason: "",
    dischargeReason: "",
    documentName: "Discharge Advice Form",
    effectiveDate: "",
    approvedBy: "",
    approvedDate: "",
    nextRevisionBy: "",
    nextRevisionDate: "",
  });

  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (patientId) {
      dispatch(fetchPatient(patientId))
        .unwrap()
        .then((patient) => {
          if (patient) {
            setFormData((prev) => ({
              ...prev,
              clientName: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
            }));
          }
        })
        .catch((err) => console.error("Failed to fetch patient details:", err));
    }
  }, [dispatch, patientId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const errors = {};
    if (!formData.clientName) errors.clientName = "Client name is required.";
    if (!formData.dischargeDate) errors.dischargeDate = "Discharge date is required.";
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Payload Formatter
  const formatPayload = (data) => {
    const toNull = (val) => (val === "" ? null : val);

    return {
      patient_id: toNull(data.patientId),
      case_id: toNull(data.caseId),
      client_name: data.clientName,
      assigned_prp: toNull(data.assignedPRP),
      psychiatric_diagnosis: toNull(data.psychiatricDiagnosis),
      primary_caretaker: toNull(data.primaryCaretaker),
      admission_date: toNull(data.admissionDate),
      discharge_date: data.dischargeDate,
      // discharge_reason: data.dischargeReason,
      // other_reason: data.dischargeReason === "Other" ? toNull(data.otherReason) : null,
      discharge_reason: toNull(data.dischargeReason),
      document_name: data.documentName,
      effective_date: toNull(data.effectiveDate),
      approved_by: toNull(data.approvedBy),
      approved_date: toNull(data.approvedDate),
      next_revision_by: toNull(data.nextRevisionBy),
      next_revision_date: toNull(data.nextRevisionDate),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      Swal.fire({
        title: "Validation Error",
        html: Object.values(validationErrors).join("<br>"),
        icon: "warning",
      });
      return;
    }

    const payload = formatPayload(formData);

    try {
      const res = await dispatch(createDischargeAdvice(payload)).unwrap();

      Swal.fire({
        title: "Success!",
        text: "Discharge Advice created successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      navigate(`/phase07/discharge-advice/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
    } catch (err) {
      const errorMsg =
        err?.errors
          ? Object.values(err.errors).flat().join("<br>")
          : err || "Failed to create discharge advice.";

      Swal.fire({
        title: "Error",
        html: errorMsg,
        icon: "error",
      });
    }
  };

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="card">
          <div className="card-body">
            {/* Validation Errors */}
            {Object.keys(validationErrors).length > 0 && (
              <div className="alert alert-warning">
                {Object.values(validationErrors).map((err, i) => (
                  <div key={i}>• {err}</div>
                ))}
              </div>
            )}

            {/* API Error */}
            {error && (
              <div className="alert alert-danger">
                <strong>Error:</strong> {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <DischargeAdviceForm
                formData={formData}
                setFormData={setFormData}
                handleChange={handleChange}
                isLoading={isLoading}
              />

              <div className="row mt-3">
                <div className="col-md-2 ms-auto">
                  <button
                    type="submit"
                    className="btn btn-primary w-100 sticky-form-submit"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-3-line me-1"></i> Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AddDischargeAdvice;